import React, { useState, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { useDropzone } from 'react-dropzone'
import { toast } from 'react-toastify'
import { ProgressBar, Badge } from 'react-bootstrap'
import { useQueryClient } from 'react-query'
import { baseUrl } from 'api/helpers'
import AuthContext from 'context/auth-context'
import { useContext } from 'react'
import axios from 'axios'
import DocumentSplitterModal from './splitter/DocumentSplitterModal'
import { MAX_SEGMENT_BYTES, MAX_SEGMENT_PAGES, MAX_SOURCE_BYTES } from './splitter/segments'
import { isBrowserImage, isPdf, isTiff } from './splitter/image-source'
import { countPages } from './splitter/page-count'

const BUSY_STATUSES = ['checking', 'review', 'pending', 'uploading']
const DONE_STATUSES = ['processing', 'error', 'cancelled']
// Counting pages reads each file into memory, so a large drop is read a few files at a time.
const COUNT_CONCURRENCY = 4

const mapWithLimit = async (items, limit, fn) => {
  const results = new Array(items.length)
  let next = 0
  const worker = async () => {
    while (next < items.length) {
      const index = next++
      results[index] = await fn(items[index])
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker))
  return results
}

const ReceiptDropzone = ({ onUploadSuccess, onUploadError, onAllUploadsComplete, scanType = 'receipt' }) => {
  const [uploadQueue, setUploadQueue] = useState([])
  // Files of the current drop already split or cancelled, for the splitter's "Document X of Y".
  const [reviewedCount, setReviewedCount] = useState(0)
  const nextId = useRef(0)
  const splitInputRef = useRef(null)
  const uploadChain = useRef(Promise.resolve())
  const queryClient = useQueryClient()
  const noun = scanType === 'invoice' ? 'invoice' : 'receipt'
  const token = useContext(AuthContext)

  const isBusy = uploadQueue.some((item) => BUSY_STATUSES.includes(item.status))
  const isUploading = uploadQueue.some((item) => ['pending', 'uploading'].includes(item.status))
  const reviewItems = uploadQueue.filter((item) => item.status === 'review')
  const reviewItem = reviewItems[0]

  const newItem = (file, status = 'pending', extra = {}) => ({
    id: nextId.current++,
    file,
    name: file.name,
    status,
    error: null,
    receiptScan: null,
    ...extra
  })

  const updateItem = (id, changes) =>
    setUploadQueue((prev) => prev.map((item) => (item.id === id ? { ...item, ...changes } : item)))

  const uploadSingleFile = async (item) => {
    const url = `${baseUrl}/receipt_scans`
    const formData = new FormData()
    formData.append('receipt_scan[receipt_image]', item.file)
    formData.append('receipt_scan[scan_type]', scanType)

    try {
      updateItem(item.id, { status: 'uploading' })

      const response = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      })

      updateItem(item.id, { status: 'processing', receiptScan: response.data.receipt_scan })

      if (onUploadSuccess) {
        onUploadSuccess(response.data.receipt_scan)
      }

      return { success: true, data: response.data }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || 'Failed to upload'

      updateItem(item.id, { status: 'error', error: errorMessage })

      if (onUploadError) {
        onUploadError(error)
      }

      return { success: false, error }
    }
  }

  const uploadItems = async (items) => {
    // Upload files sequentially
    const results = []
    for (const item of items) {
      results.push(await uploadSingleFile(item))
    }

    queryClient.invalidateQueries(['receipt-scans'])

    const successful = results.filter((result) => result.success).length
    const failed = results.length - successful

    if (successful > 0 && failed === 0) {
      toast.success(`${successful} ${noun}${successful > 1 ? 's' : ''} uploaded successfully`)
    } else if (successful > 0 && failed > 0) {
      toast.warning(`${successful} uploaded, ${failed} failed`)
    } else if (failed > 0) {
      toast.error(`Failed to upload ${failed} ${noun}${failed > 1 ? 's' : ''}`)
    }

    if (onAllUploadsComplete) {
      onAllUploadsComplete()
    }
  }

  // Uploads run one batch at a time, so a split document's pieces queue behind files already uploading. A batch
  // that throws fails what it had left and lets the next batch run, rather than stalling every later one.
  const enqueueUploads = (items) => {
    if (items.length === 0) return
    const ids = new Set(items.map((item) => item.id))
    uploadChain.current = uploadChain.current
      .then(() => uploadItems(items))
      .catch(() => {
        setUploadQueue((prev) =>
          prev.map((item) =>
            ids.has(item.id) && ['pending', 'uploading'].includes(item.status)
              ? { ...item, status: 'error', error: 'Failed to upload' }
              : item
          )
        )
      })
  }

  const tooLarge = (item, limit) => ({ ...item, status: 'error', error: `${item.name} is too large. Maximum size is ${limit}.` })

  // A file with one page goes straight up; one with several opens the splitter, starting as a single document, so
  // nobody has to say up front which kind they have. `split` sends every file to the splitter, for a page that
  // holds several. A file the browser can't read goes up as it is when it can, and the server judges it.
  const route = async (item, split) => {
    const { file } = item
    if (file.size > MAX_SOURCE_BYTES) return tooLarge(item, '50MB')
    if (!(isPdf(file) || isTiff(file) || isBrowserImage(file))) {
      return file.size < MAX_SEGMENT_BYTES ? { ...item, status: 'pending' } : tooLarge(item, '10MB')
    }

    let pageCount = null
    try {
      pageCount = await countPages(file)
    } catch (error) {
      return { ...item, split, status: !split && file.size < MAX_SEGMENT_BYTES ? 'pending' : 'review' }
    }

    const oversized = file.size >= MAX_SEGMENT_BYTES
    if (split) return { ...item, pageCount, split, status: 'review' }
    if (pageCount > 1) return { ...item, pageCount, status: 'review' }
    // One oversized page can only get under the limit by boxing the receipts on it, which suits a photo of several;
    // a one-page PDF or scan that large is refused here rather than after the splitter opens.
    if (oversized) return isBrowserImage(file) ? { ...item, pageCount, status: 'review' } : tooLarge(item, '10MB')
    return { ...item, pageCount, status: 'pending' }
  }

  const addFiles = async (files, { split = false } = {}) => {
    if (files.length > 250) {
      toast.error('You can only upload 250 files at a time.')
      return
    }
    if (files.length === 0) return

    const checking = files.map((file) => newItem(file, 'checking'))
    setUploadQueue(checking)
    setReviewedCount(0)
    const routed = await mapWithLimit(checking, COUNT_CONCURRENCY, (item) => route(item, split))

    setUploadQueue(routed)
    enqueueUploads(routed.filter((item) => item.status === 'pending'))
  }

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      rejectedFiles.forEach((rejection) => {
        if (rejection.errors.some((e) => e.code === 'file-too-large')) {
          toast.error(`${rejection.file.name} is too large. Maximum size is 50MB.`)
        } else if (rejection.errors.some((e) => e.code === 'file-invalid-type')) {
          toast.error(`${rejection.file.name} has invalid file type.`)
        }
      })
      addFiles(acceptedFiles)
    },
    [token, scanType]
  )

  const handleSplitPick = (event) => {
    const files = Array.from(event.target.files || [])
    event.target.value = ''
    addFiles(files, { split: true })
  }

  const handleSplitConfirm = (files) => {
    const pieces = files.map((file) => newItem(file))
    setUploadQueue((prev) => prev.flatMap((item) => (item.id === reviewItem.id ? pieces : [item])))
    setReviewedCount((count) => count + 1)
    enqueueUploads(pieces)
  }

  const handleSplitCancel = () => {
    updateItem(reviewItem.id, { status: 'cancelled' })
    setReviewedCount((count) => count + 1)
  }

  // The other waiting files that could go up whole, as a single upload would take them. The open file keeps the
  // marks made on it, and a file picked through "Split a file" was sent here to be split.
  const uploadableWhole = (item) =>
    !item.split && item.pageCount != null && item.pageCount <= MAX_SEGMENT_PAGES && item.file.size < MAX_SEGMENT_BYTES
  const restWhole = reviewItems.slice(1).filter(uploadableWhole)

  const handleKeepRestWhole = () => {
    const ids = new Set(restWhole.map((item) => item.id))
    const pieces = restWhole.map((item) => ({ ...item, status: 'pending' }))
    setUploadQueue((prev) => prev.map((item) => (ids.has(item.id) ? { ...item, status: 'pending' } : item)))
    enqueueUploads(pieces)
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.tiff'],
      'application/pdf': ['.pdf']
    },
    maxSize: MAX_SOURCE_BYTES,
    multiple: true,
    disabled: isBusy
  })

  const clearQueue = () => {
    setUploadQueue([])
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'checking':
        return <Badge bg="light" text="primary">Checking</Badge>
      case 'review':
        return <Badge bg="light" text="primary">Waiting to split</Badge>
      case 'pending':
        return <Badge bg="secondary">Pending</Badge>
      case 'uploading':
        return <Badge bg="primary">Uploading</Badge>
      case 'processing':
        return <small className="text-success">Uploaded</small>
      case 'error':
        return <Badge bg="danger">Failed</Badge>
      case 'cancelled':
        return <small className="text-muted">Not uploaded</small>
      default:
        return null
    }
  }

  const completedCount = uploadQueue.filter((item) => DONE_STATUSES.includes(item.status)).length
  const totalCount = uploadQueue.length
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0

  return (
    <div>
      <div
        {...getRootProps()}
        className={`receipt-dropzone ${isDragActive ? 'active' : ''} ${isBusy ? 'uploading' : ''}`}
        style={{
          border: '2px dashed #ccc',
          borderRadius: '8px',
          padding: '16px',
          textAlign: 'center',
          cursor: isBusy ? 'not-allowed' : 'pointer',
          backgroundColor: isDragActive ? '#f0f8ff' : '#fafafa',
          transition: 'all 0.3s ease',
          opacity: isBusy ? 0.6 : 1
        }}
      >
        {/* react-dropzone keeps a real (hidden) file input, which is what
            cy.selectFile drives — do not try to simulate drag events. */}
        <input data-testid="receipt-dropzone-input" {...getInputProps()} />
        {isDragActive ? (
          <p className="mb-0 small">Drop the {noun}s here...</p>
        ) : (
          <div className="d-flex align-items-center justify-content-center gap-3">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ color: '#6c757d', flexShrink: 0 }}
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <div className="text-start">
              <span className="small">
                <strong>Drag & drop {noun}s here, or click to select</strong>
              </span>
              <span className="text-muted small ms-2">
                PNG, JPG, TIFF, PDF up to 50MB
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="small text-muted mt-1">
        A file with several pages opens so you can mark where each {noun} starts. Several {noun}s on one page?{' '}
        <button
          type="button"
          className="btn btn-link btn-sm p-0 align-baseline"
          onClick={() => splitInputRef.current?.click()}
          disabled={isBusy}
          data-testid="split-file-button"
        >
          Split a file
        </button>
        <input
          ref={splitInputRef}
          type="file"
          multiple
          accept="image/png,image/jpeg,image/gif,image/tiff,.tif,.tiff,application/pdf"
          className="d-none"
          onChange={handleSplitPick}
          data-testid="split-file-input"
        />
      </div>

      {reviewItem && (
        <DocumentSplitterModal
          key={reviewItem.id}
          file={reviewItem.file}
          noun={noun}
          position={reviewedCount + 1}
          total={reviewedCount + reviewItems.length}
          restWholeCount={restWhole.length}
          onCancel={handleSplitCancel}
          onConfirm={handleSplitConfirm}
          onKeepRestWhole={handleKeepRestWhole}
        />
      )}

      {/* Upload Progress Section */}
      {uploadQueue.length > 0 && (
        <div className="mt-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">
              Upload Progress ({completedCount} of {totalCount})
            </h6>
            {!isBusy && (
              <button
                className="btn btn-sm btn-light"
                onClick={clearQueue}
              >
                Close
              </button>
            )}
          </div>

          <ProgressBar
            now={progressPercent}
            label={`${progressPercent}%`}
            animated={isUploading}
            striped={isUploading}
            className="mb-3"
          />

          <div
            className="upload-queue-list"
            style={{ maxHeight: '200px', overflowY: 'auto' }}
          >
            {uploadQueue.map((item) => (
              <div
                key={item.id}
                className="d-flex justify-content-between align-items-center py-2 border-bottom"
              >
                <div className="text-truncate" style={{ maxWidth: '70%' }}>
                  <small>{item.name}</small>
                  {item.error && (
                    <div className="text-danger small">{item.error}</div>
                  )}
                </div>
                <div>{getStatusBadge(item.status)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

ReceiptDropzone.propTypes = {
  onUploadSuccess: PropTypes.func,
  onUploadError: PropTypes.func,
  onAllUploadsComplete: PropTypes.func,
  scanType: PropTypes.string
}

export default ReceiptDropzone
